import React, {forwardRef} from 'react';

import {emptyObject} from '@packages/utilities/fp/utils';

import IconButton from '../IconButton';
import {Spacer} from '../Spacer';
import {Text, type TextTruncationProps} from '../Text';
import {ArrowLeftIcon, CloseDefaultIcon} from '../../icons';
import {
  PopoverHeaderView,
  PopoverHeaderContentView,
  PopoverHeaderEmptyIconButtonView,
  PopoverTitleView,
} from './Popover.styles';
import {usePopoverContext} from './usePopover';

import type {HTMLAttributes, SpringTooling} from '../__SharedTypes';
import type {TruncationProps} from '../../hooks';

type PopoverHeaderProps = {
  /**
   * Contents of the Popover header that go to the right of the title.
   */
  children?: React.ReactNode;
  /**
   * Whether or not a back arrow icon button appears in the top-left corner of the Popover header.
   */
  hasBackButton?: boolean;
  /**
   * Whether or not a border appears at the bottom of the Popover header.
   */
  hasBorder?: boolean;
  /**
   * Whether or not an X icon button appears in the top-right corner of the Popover header.
   */
  hasCloseButton?: boolean;
  /**
   * Callback for when the back button is clicked. Used with the hasBackButton prop.
   */
  onBackButtonClick?: (event: React.MouseEvent) => void;
  /**
   * Callback for when the close button is clicked. Used with the hasCloseButton prop.
   *
   * This is a temporary prop until we fix our onOpenChange handler behavior.
   */
  _onCloseButtonClick?: (event: React.MouseEvent) => void;
  /**
   * Additional content to render after the title (right aligned)
   */
  suffix?: React.ReactNode;
  /**
   * The title of the Popover header.
   */
  title?: string;
  /**
   * Props for truncating the title text.
   */
  titleTruncationProps?: TruncationProps & {
    truncationTooltipProps?: TextTruncationProps;
  };
} & HTMLAttributes &
  SpringTooling;

export const PopoverHeader = forwardRef<HTMLDivElement, PopoverHeaderProps>(
  (
    {
      children,
      hasBackButton,
      hasBorder = true,
      hasCloseButton,
      onBackButtonClick,
      _onCloseButtonClick,
      suffix,
      title,
      titleTruncationProps = emptyObject,
      ...props
    },
    ref
  ) => {
    const {context, colorVariant: popoverColorVariant} = usePopoverContext();
    const {onOpenChange} = context;
    const hasCloseButtonAction = hasCloseButton && onOpenChange;

    // The default ghost IconButton renders dark icons, which disappear on the
    // blue PopoverContent background. Switch to the white ghost variant so the
    // back/close icons remain visible.
    const iconButtonColorVariant =
      popoverColorVariant === 'blue' ? 'white' : undefined;

    const handleCloseButtonClick = React.useCallback(
      (event: React.MouseEvent) => {
        _onCloseButtonClick?.(event);
        onOpenChange(false);
      },
      [_onCloseButtonClick, onOpenChange]
    );

    return (
      <PopoverHeaderView hasBorder={hasBorder} {...props} ref={ref} spacing={0}>
        {hasBackButton ? (
          <IconButton
            colorVariant={iconButtonColorVariant}
            Icon={ArrowLeftIcon}
            label="Back"
            onClick={onBackButtonClick}
            variant="ghost"
          />
        ) : null}
        <PopoverHeaderContentView>
          {title ? (
            <PopoverTitleView font="heading" {...titleTruncationProps}>
              {title}
            </PopoverTitleView>
          ) : null}
          {children ? (
            <Text colorVariant="secondary" flexShrink={0}>
              {children}
            </Text>
          ) : null}
        </PopoverHeaderContentView>
        {hasCloseButtonAction || suffix ? <Spacer /> : null}
        {suffix ? suffix : null}
        {hasCloseButtonAction ? (
          <IconButton
            colorVariant={iconButtonColorVariant}
            Icon={CloseDefaultIcon}
            label="Close"
            onClick={handleCloseButtonClick}
            variant="ghost"
          />
        ) : (
          <PopoverHeaderEmptyIconButtonView />
        )}
      </PopoverHeaderView>
    );
  }
);

PopoverHeader.displayName = 'PopoverHeader';
PopoverHeader.toString = PopoverHeaderView.toString;

export default PopoverHeader;
